/**
 * Rules Engine
 *
 * Evaluates cross-field rules defined in form_schema.rules.
 * Rules fire after every field change and produce actions:
 *   - block_submit: Prevents form submission with an error message
 *   - warn: Shows a warning banner but allows submission
 *   - flag: Highlights the field with an info badge
 *   - set_value: Auto-sets a field value based on conditions
 *   - require_field: Dynamically marks a field as required
 *   - hide_section: Conditionally hides/shows a section
 *   - show_field / hide_field: Per-field visibility control
 *   - show_section: Force-show a section (overrides hide_section)
 *   - unrequire_field: Dynamically removes a required flag
 *   - jump_to_section: Directs the wizard to a specific section
 *   - calculate_score: Contributes points to a score field
 *   - require_captcha: Forces a captcha challenge before submit
 *
 * The engine accepts two rule shapes:
 *   1. Native RuleDef (single `action`, flat target_* props) — original format.
 *   2. Builder LogicRule (`actions[]` array, `fieldKey`/`targetKey` props) —
 *      emitted by FormLogicBuilder. These are translated at evaluation time
 *      so saved rules work without migration.
 *
 * Rules are evaluated top-to-bottom. Multiple rules can fire simultaneously.
 */

import type { LogicAction, LogicCondition, LogicRule } from '../builder/FormLogicBuilder';

// ============================================================================
// TYPES
// ============================================================================

export type RuleAction =
  | 'block_submit' | 'warn' | 'flag' | 'set_value' | 'require_field'
  | 'hide_section' | 'show_field' | 'hide_field' | 'unrequire_field'
  | 'jump_to_section' | 'show_section' | 'calculate_score' | 'require_captcha';

export type ConditionOperator =
  '=' | '!=' | '>' | '<' | '>=' | '<=' | 'empty' | 'not_empty'
  | 'contains' | 'not_contains' | 'in';

export interface RuleDef {
  id: string;
  /** Human label for debugging/admin UI */
  label?: string;
  /** Whether the rule is active (builder `enabled` flag). Defaults to true. */
  enabled?: boolean;
  /** Field key that triggers this rule */
  trigger_field?: string;
  /** Section key that triggers this rule (for repeatable sections) */
  trigger_section?: string;
  /** Simple equality check: fires when trigger_field === trigger_value */
  trigger_value?: string | number | boolean;
  /**
   * Complex condition expression (evaluated against the field value).
   * Supports: '< N', '> N', '<= N', '>= N', '= N', '!= N',
   * 'empty', 'not_empty', 'contains:text', 'in:a,b,c'
   */
  condition?: string;
  /**
   * Multi-field condition: all must match for rule to fire.
   * Each item: { field, operator, value }
   */
  conditions?: Array<{
    field: string;
    operator: ConditionOperator;
    value?: string | number | boolean;
  }>;
  /** Action to take when rule fires */
  action: RuleAction;
  /** Message to display (for block_submit, warn, flag) */
  message?: string;
  /** Target field for set_value / require_field / show_field / hide_field */
  target_field?: string;
  /** Target section for hide_section / show_section / jump_to_section */
  target_section?: string;
  /** Value to set for set_value action */
  set_value?: unknown;
  /** Severity for flag action */
  severity?: 'info' | 'warning' | 'error';
  /** Priority for ordering — higher runs first (default: 0) */
  priority?: number;
}

export interface RuleResult {
  id: string;
  label?: string;
  action: RuleAction;
  message?: string;
  target_field?: string;
  target_section?: string;
  set_value?: unknown;
  severity?: string;
  triggered: boolean;
}

export interface RulesEvaluationResult {
  /** All rules that fired */
  firedRules: RuleResult[];
  /** Whether any block_submit rule fired */
  hasBlockers: boolean;
  /** All blocker messages */
  blockerMessages: string[];
  /** All warning messages */
  warningMessages: string[];
  /** Field keys that have flag rules active */
  flaggedFields: Map<string, { message: string; severity: string }>;
  /** Fields that rules want to set values on — { fieldKey: value } */
  valueOverrides: Record<string, unknown>;
  /** Field keys that are dynamically required by rules */
  dynamicRequired: Set<string>;
  /** Field keys whose required flag is dynamically removed by rules */
  dynamicUnrequired: Set<string>;
  /** Section keys that should be hidden */
  hiddenSections: Set<string>;
  /** Section keys explicitly shown (overrides hiddenSections) */
  shownSections: Set<string>;
  /** Field keys that should be hidden */
  hiddenFields: Set<string>;
  /** Field keys explicitly shown (overrides hiddenFields) */
  shownFields: Set<string>;
  /** Section key the wizard should jump to (last fired jump_to_section wins) */
  jumpTarget: string | null;
  /** Whether a rule forced captcha verification before submit */
  captchaRequired: boolean;
  /** Accumulated score contributions per target field */
  scoreOverrides: Record<string, number>;
}

// ============================================================================
// BUILDER → NATIVE TRANSLATION
// ============================================================================
//
// FormLogicBuilder emits LogicRule[] with an `actions[]` array and
// camelCase props (fieldKey, targetKey, equals, block_submission…).
// The engine consumes native RuleDef[] (single `action`, snake_case props).
// `normalizeRules` detects the shape and flattens one builder rule's N
// actions into N native rules sharing the same conditions, so saved
// builder rules fire without any migration step.

/** Builder condition operator → native condition operator */
const BUILDER_OP_MAP: Record<string, ConditionOperator> = {
  equals: '=',
  not_equals: '!=',
  contains: 'contains',
  not_contains: 'not_contains',
  greater_than: '>',
  less_than: '<',
  greater_than_or_equal: '>=',
  less_than_or_equal: '<=',
  is_empty: 'empty',
  is_not_empty: 'not_empty',
};

/** Builder action type → native action + prop mapping */
function translateBuilderAction(
  action: { type?: string; targetKey?: string; value?: unknown },
): { action: RuleAction } & Partial<Omit<RuleDef, 'action'>> {
  switch (action.type) {
    case 'show_field':
      return { action: 'show_field', target_field: action.targetKey };
    case 'hide_field':
      return { action: 'hide_field', target_field: action.targetKey };
    case 'require_field':
      return { action: 'require_field', target_field: action.targetKey };
    case 'unrequire_field':
      return { action: 'unrequire_field', target_field: action.targetKey };
    case 'set_value':
      return { action: 'set_value', target_field: action.targetKey, set_value: action.value };
    case 'jump_to_section':
      return { action: 'jump_to_section', target_section: action.targetKey };
    case 'show_section':
      return { action: 'show_section', target_section: action.targetKey };
    case 'hide_section':
      return { action: 'hide_section', target_section: action.targetKey };
    case 'block_submission':
      return { action: 'block_submit', message: action.value !== undefined ? String(action.value) : undefined };
    case 'show_warning':
      return { action: 'warn', message: action.value !== undefined ? String(action.value) : undefined };
    case 'calculate_score':
      return { action: 'calculate_score', target_field: action.targetKey, set_value: action.value };
    case 'require_captcha':
      return { action: 'require_captcha' };
    default:
      return { action: 'flag' as RuleAction };
  }
}

/**
 * Normalize an arbitrary rule array (builder or native shape) into RuleDef[].
 * Builder rules with multiple actions are flattened into one RuleDef per action.
 * Disabled builder rules (enabled === false) are dropped.
 */
function normalizeRules(input: unknown[]): RuleDef[] {
  const out: RuleDef[] = [];
  for (const raw of input) {
    if (!raw || typeof raw !== 'object') continue;
    const r = raw as Record<string, unknown>;

    // Builder shape: has an `actions` array
    if (Array.isArray(r.actions)) {
      if (r.enabled === false) continue; // honor the builder enabled flag
      const conditions = Array.isArray(r.conditions)
        ? r.conditions.map((c: unknown) => {
            const cond = (c && typeof c === 'object' ? c : {}) as Record<string, unknown>;
            const op = String(cond.operator ?? '');
            const value = cond.value;
            return {
              field: String(cond.fieldKey ?? cond.field ?? ''),
              operator: (BUILDER_OP_MAP[op] ?? (op as ConditionOperator)) ?? '=',
              ...(typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean'
                ? { value }
                : {}),
            };
          })
        : undefined;
      for (const action of r.actions) {
        if (!action || typeof action !== 'object') continue;
        const a = action as { id?: unknown; type?: unknown; targetKey?: unknown; value?: unknown };
        if (!a.type) continue;
        out.push({
          id: `${r.id || 'rule'}_${a.id || a.type}`,
          label: r.name !== undefined ? String(r.name) : undefined,
          enabled: r.enabled !== false,
          conditions,
          ...translateBuilderAction({
            type: String(a.type),
            targetKey: a.targetKey !== undefined ? String(a.targetKey) : undefined,
            value: a.value,
          }),
        });
      }
      continue;
    }

    // Native shape: has a single `action` string
    if (typeof r.action === 'string') {
      if (r.enabled === false) continue;
      out.push(r as unknown as RuleDef);
    }
  }
  return out;
}

// ============================================================================
// NATIVE → BUILDER TRANSLATION
// ============================================================================
//
// The inverse direction of `normalizeRules`: adapts whatever is stored in
// form_schema.rules (native RuleDef, builder LogicRule, or a mix) into the
// LogicRule[] shape the designer's FormLogicCanvas can render and edit.
// Without it a native rule crashes the canvas (`rule.actions[0]` on
// undefined inside rules.map).
//
// Mapping notes:
//   - label → name; trigger_field/trigger_value/condition/conditions →
//     conditions[]; single `action` → single-item actions[].
//   - Operators >=, <= and `in` have no builder equivalent — approximated
//     as >, < and equals respectively.
//   - Native-only props (severity, priority, trigger_section, label, the
//     original action/target_*/message/set_value props) are preserved on
//     the returned object so a round-trip through the editor does not
//     silently strip data the canvas cannot represent. The engine still
//     evaluates the builder shape alone, so once a rule is saved from the
//     canvas its behaviour is defined by the builder fields.

const BUILDER_OPS: Set<string> = new Set([
  'equals', 'not_equals', 'contains', 'not_contains',
  'greater_than', 'less_than', 'is_empty', 'is_not_empty',
]);

/** Native/other operator string → builder operator. */
const NATIVE_OP_TO_BUILDER: Record<string, LogicCondition['operator']> = {
  '=': 'equals',
  '!=': 'not_equals',
  '>': 'greater_than',
  '<': 'less_than',
  '>=': 'greater_than', // no >= in builder — direction preserved, boundary lost
  '<=': 'less_than',    // same caveat
  contains: 'contains',
  not_contains: 'not_contains',
  in: 'equals',         // no set-membership op — comma list kept as the value
  empty: 'is_empty',
  not_empty: 'is_not_empty',
};

const NATIVE_ACTION_TO_BUILDER: Record<string, LogicAction['type']> = {
  warn: 'show_warning',
  flag: 'show_warning', // flag badge semantics not representable in the canvas
  block_submit: 'block_submission',
  require_field: 'require_field',
  unrequire_field: 'unrequire_field',
  set_value: 'set_value',
  hide_section: 'hide_section',
  show_section: 'show_section',
  show_field: 'show_field',
  hide_field: 'hide_field',
  jump_to_section: 'jump_to_section',
  calculate_score: 'calculate_score',
  require_captcha: 'require_captcha',
};

function toBuilderOperator(op: unknown): LogicCondition['operator'] {
  const s = String(op ?? '');
  if (BUILDER_OPS.has(s)) return s as LogicCondition['operator'];
  return NATIVE_OP_TO_BUILDER[s] ?? 'equals';
}

function toBuilderCondition(raw: unknown, fallbackId: string): LogicCondition {
  const c = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;
  return {
    id: String(c.id ?? fallbackId),
    fieldKey: String(c.fieldKey ?? c.field ?? ''),
    operator: toBuilderOperator(c.operator),
    value: c.value === undefined || c.value === null ? '' : String(c.value),
  };
}

/** Parse a legacy `condition` expression string into a builder condition. */
function expressionToBuilderCondition(
  fieldKey: string,
  expression: string,
  id: string,
): LogicCondition {
  const t = expression.trim();
  if (t === 'empty') return { id, fieldKey, operator: 'is_empty', value: '' };
  if (t === 'not_empty') return { id, fieldKey, operator: 'is_not_empty', value: '' };
  if (t.startsWith('contains:')) {
    return { id, fieldKey, operator: 'contains', value: t.slice('contains:'.length).trim() };
  }
  if (t.startsWith('in:')) {
    return { id, fieldKey, operator: 'equals', value: t.slice('in:'.length).trim() };
  }
  const m = t.match(/^(<=|>=|!=|<|>|=)\s*(.+)$/);
  if (m) return { id, fieldKey, operator: toBuilderOperator(m[1]), value: m[2].trim() };
  return { id, fieldKey, operator: 'equals', value: t };
}

function toBuilderAction(raw: Record<string, unknown>, fallbackId: string): LogicAction {
  return {
    id: String(raw.action_id ?? fallbackId),
    type: NATIVE_ACTION_TO_BUILDER[String(raw.action ?? '')] ?? 'show_warning',
    targetKey: (raw.target_field ?? raw.target_section) as string | undefined,
    value:
      raw.message !== undefined && raw.message !== null
        ? String(raw.message)
        : raw.set_value !== undefined && raw.set_value !== null
          ? String(raw.set_value)
          : undefined,
  };
}

/**
 * Adapt form_schema.rules (native RuleDef, builder LogicRule, or mixed)
 * into LogicRule[] for the designer's logic canvas.
 */
export function toBuilderLogicRules(input: unknown): LogicRule[] {
  if (!Array.isArray(input)) return [];

  return input.map((raw: unknown, i) => {
    if (!raw || typeof raw !== 'object') {
      return { id: `rule_${i}`, name: `Rule ${i + 1}`, enabled: true, conditions: [], actions: [] };
    }
    const r = raw as Record<string, unknown>;

    const base = {
      id: String(r.id ?? `rule_${i}`),
      name: String(r.name ?? r.label ?? `Rule ${i + 1}`),
      enabled: r.enabled !== false,
    };

    if (Array.isArray(r.actions)) {
      // Already builder shape — fill in any missing pieces.
      return {
        ...r,
        ...base,
        conditions: (Array.isArray(r.conditions) ? r.conditions : []).map(
          (c: unknown, ci: number) => toBuilderCondition(c, `${base.id}_cond_${ci}`),
        ),
        actions: r.actions.map((a: unknown, ai: number) => {
          const ao = (a && typeof a === 'object' ? a : {}) as Record<string, unknown>;
          return { ...ao, id: String(ao.id ?? `${base.id}_action_${ai}`) };
        }),
      } as LogicRule;
    }

    // Native RuleDef → single-action builder rule.
    const conditions: LogicCondition[] =
      Array.isArray(r.conditions) && r.conditions.length
        ? r.conditions.map((c: unknown, ci: number) =>
            toBuilderCondition(c, `${base.id}_cond_${ci}`),
          )
        : r.trigger_field
          ? [
              r.trigger_value !== undefined
                ? {
                    id: `${base.id}_cond_0`,
                    fieldKey: String(r.trigger_field),
                    operator: 'equals' as const,
                    value: String(r.trigger_value),
                  }
                : typeof r.condition === 'string' && r.condition.trim()
                  ? expressionToBuilderCondition(
                      String(r.trigger_field),
                      r.condition,
                      `${base.id}_cond_0`,
                    )
                  : {
                      id: `${base.id}_cond_0`,
                      fieldKey: String(r.trigger_field),
                      operator: 'is_not_empty' as const,
                      value: '',
                    },
            ]
          : [];

    return {
      ...r,
      ...base,
      conditions,
      actions: typeof r.action === 'string'
        ? [toBuilderAction(r, `${base.id}_action_0`)]
        : [],
    } as LogicRule;
  });
}

// ============================================================================
// ENGINE
// ============================================================================

/**
 * Evaluate all rules against current form data.
 * Call this on every formData change (debounce in UI if needed for perf).
 * 
 * @param ruleDefs - Array of rule definitions from form_schema.rules
 * @param formData - Current form data
 * @param changedField - Optional: the field key that just changed (for targeted eval)
 * @returns Aggregated evaluation results
 */
export function evaluateRules(
  ruleDefs: Array<RuleDef | LogicRule> | unknown,
  formData: Record<string, unknown>,
  _changedField?: string
): RulesEvaluationResult {
  const result: RulesEvaluationResult = {
    firedRules: [],
    hasBlockers: false,
    blockerMessages: [],
    warningMessages: [],
    flaggedFields: new Map(),
    valueOverrides: {},
    dynamicRequired: new Set(),
    dynamicUnrequired: new Set(),
    hiddenSections: new Set(),
    shownSections: new Set(),
    hiddenFields: new Set(),
    shownFields: new Set(),
    jumpTarget: null,
    captchaRequired: false,
    scoreOverrides: {},
  };

  if (!Array.isArray(ruleDefs) || !ruleDefs.length) return result;

  // Translate builder-shaped rules into native RuleDef[] (flattening actions)
  const normalized = normalizeRules(ruleDefs);
  if (!normalized.length) return result;

  // Sort by priority descending (higher priority first)
  const sorted = [...normalized].sort((a, b) => (b.priority || 0) - (a.priority || 0));

  for (const rule of sorted) {
    const triggered = evaluateRule(rule, formData);
    const ruleResult: RuleResult = {
      id: rule.id,
      label: rule.label,
      action: rule.action,
      message: rule.message,
      target_field: rule.target_field,
      target_section: rule.target_section,
      set_value: rule.set_value,
      severity: rule.severity,
      triggered,
    };

    if (triggered) {
      result.firedRules.push(ruleResult);

      switch (rule.action) {
        case 'block_submit':
          result.hasBlockers = true;
          if (rule.message) result.blockerMessages.push(rule.message);
          break;

        case 'warn':
          if (rule.message) result.warningMessages.push(rule.message);
          break;

        case 'flag':
          if (rule.target_field || rule.trigger_field) {
            result.flaggedFields.set(
              rule.target_field || rule.trigger_field!,
              { message: rule.message || '', severity: rule.severity || 'warning' }
            );
          }
          break;

        case 'set_value':
          if (rule.target_field && rule.set_value !== undefined) {
            result.valueOverrides[rule.target_field] = rule.set_value;
          }
          break;

        case 'require_field':
          if (rule.target_field) {
            result.dynamicRequired.add(rule.target_field);
            // require overrides any prior unrequire for the same field
            result.dynamicUnrequired.delete(rule.target_field);
          }
          break;

        case 'unrequire_field':
          if (rule.target_field) {
            // only unrequire if no require_field rule also fired for this field
            if (!result.dynamicRequired.has(rule.target_field)) {
              result.dynamicUnrequired.add(rule.target_field);
            }
          }
          break;

        case 'hide_section':
          if (rule.target_section) {
            result.hiddenSections.add(rule.target_section);
            result.shownSections.delete(rule.target_section);
          }
          break;

        case 'show_section':
          if (rule.target_section) {
            result.shownSections.add(rule.target_section);
            result.hiddenSections.delete(rule.target_section);
          }
          break;

        case 'hide_field':
          if (rule.target_field) {
            result.hiddenFields.add(rule.target_field);
            result.shownFields.delete(rule.target_field);
          }
          break;

        case 'show_field':
          if (rule.target_field) {
            result.shownFields.add(rule.target_field);
            result.hiddenFields.delete(rule.target_field);
          }
          break;

        case 'jump_to_section':
          if (rule.target_section) {
            result.jumpTarget = rule.target_section;
          }
          break;

        case 'calculate_score':
          if (rule.target_field) {
            const points = Number(rule.set_value);
            if (!isNaN(points)) {
              result.scoreOverrides[rule.target_field] =
                (result.scoreOverrides[rule.target_field] || 0) + points;
            }
          }
          break;

        case 'require_captcha':
          result.captchaRequired = true;
          break;
      }
    }
  }

  return result;
}

// ============================================================================
// RULE EVALUATION
// ============================================================================

function evaluateRule(rule: RuleDef, formData: Record<string, unknown>): boolean {
  // Multi-condition rules: all conditions must match
  if (rule.conditions?.length) {
    return rule.conditions.every(cond => evaluateConditionItem(cond, formData));
  }

  // Single trigger_field rules
  if (rule.trigger_field) {
    const fieldValue = getFieldValue(formData, rule.trigger_field);

    // Simple equality check
    if (rule.trigger_value !== undefined) {
      return matchesValue(fieldValue, rule.trigger_value);
    }

    // Complex condition expression
    if (rule.condition) {
      return evaluateConditionExpression(fieldValue, rule.condition);
    }

    // If no condition, just check if field has a value
    return fieldValue !== null && fieldValue !== undefined && fieldValue !== '';
  }

  // Section-level trigger (for repeatable sections)
  if (rule.trigger_section) {
    const sectionData = formData[rule.trigger_section];
    if (!Array.isArray(sectionData)) return false;

    // Check if any row in the section matches the condition
    return sectionData.some(row => {
      if (rule.trigger_field) {
        const val = getFieldValue(row, rule.trigger_field);
        if (rule.trigger_value !== undefined) return matchesValue(val, rule.trigger_value);
        if (rule.condition) return evaluateConditionExpression(val, rule.condition);
      }
      return false;
    });
  }

  return false;
}

function evaluateConditionItem(
  cond: { field: string; operator: string; value?: string | number | boolean },
  formData: Record<string, unknown>
): boolean {
  const actual = getFieldValue(formData, cond.field);

  switch (cond.operator) {
    case '=': return matchesValue(actual, cond.value);
    case '!=': return !matchesValue(actual, cond.value);
    case '>': return toNumber(actual) !== null && toNumber(actual)! > Number(cond.value);
    case '<': return toNumber(actual) !== null && toNumber(actual)! < Number(cond.value);
    case '>=': return toNumber(actual) !== null && toNumber(actual)! >= Number(cond.value);
    case '<=': return toNumber(actual) !== null && toNumber(actual)! <= Number(cond.value);
    case 'empty': return actual === null || actual === undefined || actual === '';
    case 'not_empty': return actual !== null && actual !== undefined && actual !== '';
    case 'contains': return String(actual || '').toLowerCase().includes(String(cond.value || '').toLowerCase());
    case 'not_contains': return !String(actual || '').toLowerCase().includes(String(cond.value || '').toLowerCase());
    case 'in': {
      const options = String(cond.value || '').split(',').map(s => s.trim().toLowerCase());
      return options.includes(String(actual || '').toLowerCase());
    }
    default: return false;
  }
}

/**
 * Parse and evaluate a condition expression string.
 * Formats:
 *   "< 1.0" — numeric less than
 *   "> 100" — numeric greater than
 *   "= pass" — equality
 *   "!= fail" — inequality
 *   "empty" — value is empty
 *   "not_empty" — value is not empty
 *   "contains:earth" — string contains
 *   "in:pass,na,conditional" — value in list
 */
function evaluateConditionExpression(value: unknown, expression: string): boolean {
  const trimmed = expression.trim();

  if (trimmed === 'empty') {
    return value === null || value === undefined || value === '';
  }
  if (trimmed === 'not_empty') {
    return value !== null && value !== undefined && value !== '';
  }
  if (trimmed.startsWith('contains:')) {
    const search = trimmed.slice('contains:'.length).trim();
    return String(value || '').toLowerCase().includes(search.toLowerCase());
  }
  if (trimmed.startsWith('in:')) {
    const options = trimmed.slice('in:'.length).split(',').map(s => s.trim().toLowerCase());
    return options.includes(String(value || '').toLowerCase());
  }

  // Operator + value patterns
  const opMatch = trimmed.match(/^(<=|>=|!=|<|>|=)\s*(.+)$/);
  if (opMatch) {
    const [, op, rhs] = opMatch;
    const rhsTrimmed = rhs.trim();
    const numericRhs = Number(rhsTrimmed);
    const numericVal = toNumber(value);

    // Try numeric comparison first
    if (!isNaN(numericRhs) && numericVal !== null) {
      switch (op) {
        case '<': return numericVal < numericRhs;
        case '>': return numericVal > numericRhs;
        case '<=': return numericVal <= numericRhs;
        case '>=': return numericVal >= numericRhs;
        case '=': return numericVal === numericRhs;
        case '!=': return numericVal !== numericRhs;
      }
    }

    // Fall back to string comparison
    const strVal = String(value || '').toLowerCase();
    const strRhs = rhsTrimmed.toLowerCase();
    switch (op) {
      case '=': return strVal === strRhs;
      case '!=': return strVal !== strRhs;
      default: return false;
    }
  }

  // Bare value = equality check
  return String(value || '').toLowerCase() === trimmed.toLowerCase();
}

// ============================================================================
// HELPERS
// ============================================================================

/**
 * Get field value from formData, handling nested pass_fail / measurement objects.
 */
function getFieldValue(data: Record<string, unknown>, fieldKey: string): unknown {
  const val = data[fieldKey];
  if (val === null || val === undefined || typeof val !== 'object') return val;

  // pass_fail: { result: 'pass'|'fail'|'na', comment?: string }
  if ('result' in val) {
    return (val as { result?: unknown }).result;
  }

  // measurement: might be { value, unit } or raw number
  if ('value' in val && !Array.isArray(val)) {
    return (val as { value?: unknown }).value;
  }

  return val;
}

function matchesValue(actual: unknown, expected: unknown): boolean {
  if (actual === null || actual === undefined || expected === null || expected === undefined) return false;
  return String(actual).toLowerCase() === String(expected).toLowerCase();
}

function toNumber(val: unknown): number | null {
  if (val === null || val === undefined || val === '') return null;
  if (typeof val === 'number') return val;
  const n = Number(val);
  return isNaN(n) ? null : n;
}

// ============================================================================
// SECTION BRANCHING
// ============================================================================
//
// Section Settings allow configuring `goto_section` (static next section) and
// `branching_rules` (conditional jumps based on field values). DynamicFormBuilder
// (public-form wizard) honors goto_section; OperationalFormModal renders
// linearly and historically ignored both. This helper computes the set of
// section keys that should be hidden because a prior section's branching
// jumps over them.
//
// Semantics for linear rendering:
//   - When a section's branching rule matches (or its static goto_section is
//     set), the sections strictly between it and the jump target are hidden.
//   - A target of '__submit' hides every section after the current one.
//   - Branching only applies once the trigger field has a value, so unfilled
//     sections show all subsequent sections by default.

export interface BranchingSection {
  key: string;
  goto_section?: string | null;
  branching_rules?: Array<{
    field_key: string;
    operator?: string;
    value?: unknown;
    goto_section: string;
  }>;
}

/**
 * Compute the set of section keys that should be hidden due to branching.
 * `sections` must be in display order (sorted by sort_order).
 */
export function resolveBranchingHiddenSections(
  sections: BranchingSection[],
  formData: Record<string, unknown>,
): Set<string> {
  const hidden = new Set<string>();
  if (!sections.length) return hidden;

  const hasBranching = sections.some(
    s => s.goto_section || (Array.isArray(s.branching_rules) && s.branching_rules.length > 0),
  );
  if (!hasBranching) return hidden;

  const indexByKey = new Map<string, number>();
  sections.forEach((s, i) => indexByKey.set(s.key, i));

  for (let i = 0; i < sections.length; i++) {
    const section = sections[i];
    if (!section) continue;

    // Resolve the effective jump target for this section
    let target: string | null = null;

    if (Array.isArray(section.branching_rules) && section.branching_rules.length > 0) {
      // First matching branching rule wins
      for (const rule of section.branching_rules) {
        if (!rule || !rule.field_key || !rule.goto_section) continue;
        const actual = getFieldValue(formData, rule.field_key);
        const op = rule.operator || 'equals';
        if (matchBranchingCondition(actual, op, rule.value)) {
          target = rule.goto_section;
          break;
        }
      }
    }

    // Fall back to the static goto_section
    if (target === null && section.goto_section) {
      target = section.goto_section;
    }

    if (target === null) continue;

    if (target === '__submit') {
      // Hide every section after this one
      for (let j = i + 1; j < sections.length; j++) {
        hidden.add(sections[j]!.key);
      }
      continue;
    }

    const targetIdx = indexByKey.get(target);
    if (targetIdx === undefined) continue;

    // Hide sections strictly between current and target.
    // Forward jump: hide (i, targetIdx). Backward jump: hide nothing
    // (the user may need to revisit earlier sections).
    if (targetIdx > i + 1) {
      for (let j = i + 1; j < targetIdx; j++) {
        hidden.add(sections[j]!.key);
      }
    }
  }

  return hidden;
}

function matchBranchingCondition(actual: unknown, operator: string, expected: unknown): boolean {
  switch (operator) {
    case 'equals':
    case '=':
      return matchesValue(actual, expected);
    case 'not_equals':
    case '!=':
      return !matchesValue(actual, expected);
    case 'contains':
      return String(actual || '').toLowerCase().includes(String(expected || '').toLowerCase());
    case 'is_empty':
    case 'empty':
      return actual === null || actual === undefined || actual === '';
    case 'is_not_empty':
    case 'not_empty':
      return actual !== null && actual !== undefined && actual !== '';
    case 'greater_than':
    case '>': {
      const a = toNumber(actual);
      return a !== null && a > Number(expected);
    }
    case 'less_than':
    case '<': {
      const a = toNumber(actual);
      return a !== null && a < Number(expected);
    }
    default:
      return matchesValue(actual, expected);
  }
}
